import { useEffect, useRef, useState } from "react";
import { Eye, Users, Clock, Star, CalendarDays, Download, ArrowUp, ArrowDown, MapPin, BadgeCheck, ExternalLink, Loader2 } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { api, resolveUrl } from "@/lib/api";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";

const AGE_COLORS = { "0-5": "#0ea5e9", "6-12": "#f59e0b", "13-17": "#ec4899" };
const Card = ({ children, className = "", testid }) => <div data-testid={testid} className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${className}`}>{children}</div>;
const CardTitle = ({ icon: Icon, color, children }) => <h3 className="flex items-center gap-2 font-display font-bold text-slate-900 mb-3"><Icon size={20} className={color} />{children}</h3>;

const Kpi = ({ icon: Icon, bg, value, label, cur, prev, testid }) => {
  const pct = prev ? Math.round(((cur - prev) / prev) * 100) : null;
  return (
    <Card testid={testid} className="!p-4">
      <span className={`h-10 w-10 grid place-items-center rounded-xl ${bg}`}><Icon size={20} /></span>
      <p className="mt-2 text-2xl font-extrabold font-display text-slate-900">{value.toLocaleString("el-GR")}</p>
      <p className="text-sm text-slate-600 leading-tight">{label}</p>
      {pct !== null && (
        <p className={`mt-1.5 text-sm font-bold flex items-center gap-0.5 ${pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
          {pct >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}{pct >= 0 ? "+" : ""}{pct}% <span className="font-normal text-slate-500 ml-1">σε σχέση με πριν</span>
        </p>
      )}
    </Card>
  );
};

const ReviewsCard = ({ r }) => (
  <Card testid="stats-reviews">
    <CardTitle icon={Star} color="text-amber-400 fill-amber-400">Αξιολογήσεις</CardTitle>
    <div className="flex items-center gap-6">
      <div className="text-center">
        <p className="text-4xl font-extrabold font-display text-slate-900">{r.avg.toFixed(1)}</p>
        <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} className={n <= Math.round(r.avg) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}</div>
        <p className="text-xs text-slate-500 mt-1">({r.count} αξιολογήσεις)</p>
      </div>
      <div className="flex-1 space-y-1">
        {[5, 4, 3, 2, 1].map((n) => (
          <div key={n} className="flex items-center gap-2 text-xs">
            <span className="w-6 text-slate-600">{n}★</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${r.count ? (r.dist[n] / r.count) * 100 : 0}%` }} /></div>
            <span className="w-6 text-right font-semibold">{r.dist[n]}</span>
          </div>
        ))}
      </div>
    </div>
  </Card>
);

const AgesCard = ({ ages }) => {
  const data = ages.map((a) => ({ name: `${a} ετών`, value: 1, color: AGE_COLORS[a] || "#64748b" }));
  return (
    <Card testid="stats-age-groups">
      <CardTitle icon={Users} color="text-violet-600">Ηλικιακές ομάδες</CardTitle>
      {data.length ? (
        <div className="flex items-center gap-4">
          <div className="h-32 w-32"><ResponsiveContainer><PieChart><Pie data={data} dataKey="value" innerRadius={34} outerRadius={56} paddingAngle={3}>{data.map((d) => <Cell key={d.name} fill={d.color} />)}</Pie></PieChart></ResponsiveContainer></div>
          <ul className="space-y-1.5 text-sm">{data.map((d) => <li key={d.name} className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: d.color }} />{d.name}</li>)}</ul>
        </div>
      ) : <p className="text-sm text-slate-500">Δεν έχουν δηλωθεί ηλικιακές ομάδες για αυτή την επιχείρηση.</p>}
    </Card>
  );
};

const fmtDay = (d) => `${+d.slice(8, 10)}/${+d.slice(5, 7)}`;

export const BusinessStats = ({ businesses }) => {
  const [bizId, setBizId] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const ref = useRef();
  const sorted = [...businesses].sort((a, b) => (a.name || "").localeCompare(b.name || "", "el"));

  useEffect(() => { if (!bizId && sorted[0]) setBizId(sorted[0].id); }, [businesses]); // eslint-disable-line
  useEffect(() => {
    if (!bizId) return;
    setData(null);
    api.get(`/admin/stats/${bizId}`, { params: { days } }).then((r) => setData(r.data)).catch(() => {});
  }, [bizId, days]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
      const canvas = await html2canvas(ref.current, { scale: 2, useCORS: true, backgroundColor: "#f8fafc" });
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const w = pdf.internal.pageSize.getWidth(), h = (canvas.height * w) / canvas.width, pageH = pdf.internal.pageSize.getHeight();
      for (let y = 0; y < h; y += pageH) { if (y) pdf.addPage(); pdf.addImage(canvas.toDataURL("image/jpeg", 0.9), "JPEG", 0, -y, w, h); }
      pdf.save(`SkillCity-στατιστικά-${data.business.name}.pdf`);
    } finally { setExporting(false); }
  };

  const b = data?.business;
  const tags = b ? [SPORTS[b.sport]?.title, SUBCATEGORIES[b.subcategory]?.title, ...(b.audiences || []).map((a) => (a === "paidia" ? "Παιδιά" : "Ενήλικες"))].filter(Boolean) : [];
  const sel = "h-11 px-3 rounded-xl bg-white border border-slate-200 text-sm font-semibold outline-none focus:ring-2 ring-primary";

  return (
    <section data-testid="business-stats">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-3xl font-extrabold text-[#0B2454]">Στατιστικά Επιχείρησης</h2>
          <p className="text-slate-500">Δείτε την απόδοση της καταχώρισης στο SkillCity</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={bizId} onChange={(e) => setBizId(e.target.value)} className={`${sel} max-w-[280px]`} data-testid="stats-business-select">
            {sorted.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <label className="relative"><CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <select value={days} onChange={(e) => setDays(+e.target.value)} className={`${sel} pl-9`} data-testid="stats-days-select">
              <option value={7}>Τελευταίες 7 ημέρες</option><option value={30}>Τελευταίες 30 ημέρες</option><option value={90}>Τελευταίες 90 ημέρες</option>
            </select>
          </label>
          <button onClick={exportPdf} disabled={!data || exporting} data-testid="stats-export-pdf" className="h-11 px-4 rounded-xl bg-[#0B2454] text-white text-sm font-bold inline-flex items-center gap-2 hover:brightness-125 disabled:opacity-50 transition-[filter]">
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Εξαγωγή αναφοράς (PDF)
          </button>
        </div>
      </div>

      {!data ? <div className="py-20 grid place-items-center text-slate-500"><Loader2 className="animate-spin" /></div> : (
        <div ref={ref} className="space-y-4 bg-slate-50 rounded-2xl" data-testid="stats-report">
          <Card className="flex items-center gap-4" testid="stats-business-card">
            {(b.cover_image || data.photo) && <img src={resolveUrl(b.cover_image || data.photo)} alt="" crossOrigin="anonymous" className="h-24 w-32 rounded-xl object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-display text-xl font-extrabold text-[#0B2454] flex items-center gap-1.5">{b.name}{b.verified && <BadgeCheck size={18} className="text-primary" />}</p>
              {b.city && <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin size={14} />{b.city}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">{tags.map((tg) => <span key={tg} className="px-2.5 py-0.5 rounded-lg bg-slate-100 text-xs font-semibold text-slate-700">{tg}</span>)}</div>
            </div>
            <a href={`/activity/${b.id}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-sky-50 text-primary text-sm font-semibold hover:bg-sky-100" data-testid="stats-view-listing"><ExternalLink size={15} /> Προβολή καταχώρισης</a>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="stats-kpi-total" icon={Eye} bg="bg-sky-100 text-sky-600" value={data.total_views} label="Συνολικές προβολές καταχώρισης" />
            <Kpi testid="stats-kpi-period" icon={Eye} bg="bg-blue-100 text-blue-600" value={data.period_views} label={`Προβολές (${days} ημέρες)`} cur={data.period_views} prev={data.prev_period_views} />
            <Kpi testid="stats-kpi-unique" icon={Users} bg="bg-emerald-100 text-emerald-600" value={data.unique_users} label="Μοναδικοί χρήστες" cur={data.unique_users} prev={data.prev_unique_users} />
            <Kpi testid="stats-kpi-rating" icon={Star} bg="bg-amber-100 text-amber-500" value={data.reviews.count} label={`Αξιολογήσεις (μ.ό. ${data.reviews.avg.toFixed(1)})`} />
          </div>

          <Card testid="stats-daily-chart">
            <CardTitle icon={Eye} color="text-blue-600">Προβολές καταχώρισης</CardTitle>
            <div className="h-60"><ResponsiveContainer>
              <AreaChart data={data.daily} margin={{ left: -20, right: 10 }}>
                <defs><linearGradient id="vg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={fmtDay} formatter={(v) => [`${v} προβολές`, ""]} />
                <Area type="monotone" dataKey="views" stroke="#2563eb" strokeWidth={2.5} fill="url(#vg)" dot={{ r: 2.5 }} />
              </AreaChart>
            </ResponsiveContainer></div>
          </Card>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card testid="stats-hours-chart">
              <CardTitle icon={Clock} color="text-emerald-600">Δημοφιλείς ώρες προβολής</CardTitle>
              <div className="h-40"><ResponsiveContainer>
                <BarChart data={data.hours.map((v, h) => ({ h: String(h).padStart(2, "0"), v }))} margin={{ left: -28 }}>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="h" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v} προβολές`, ""]} labelFormatter={(h) => `${h}:00`} />
                  <Bar dataKey="v" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer></div>
            </Card>
            <AgesCard ages={data.age_groups} />
            <ReviewsCard r={data.reviews} />
          </div>
        </div>
      )}
    </section>
  );
};
